import React, { useState } from "react";
import { UnifiedRecipeCard } from "../components/recipe/UnifiedRecipeCard";
import { CookingInterface } from "../components/recipe/CookingInterface";
import { MealPlanGrid } from "../components/recipe/MealPlanGrid";
import { ShoppingListGenerator } from "../components/recipe/ShoppingListGenerator";
// AppFrame removed: route-level layout wraps pages
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { ChefHat, Calendar, ShoppingCart, Play } from "lucide-react";

// Demo data
const sampleRecipes = [
  {
    id: "1",
    title: "Creamy Garlic Parmesan Pasta",
    imageUrl:
      "https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400",
    prepTime: 10,
    cookTime: 20,
    servings: 4,
    difficulty: "Easy" as const,
    tags: ["Vegetarian", "Quick"],
    description:
      "A rich and creamy pasta dish with garlic and parmesan cheese.",
    steps: [
      {
        id: "1",
        instruction:
          "Bring a large pot of salted water to boil. Add pasta and cook according to package directions until al dente.",
        duration: 10,
        ingredients: ["12 oz pasta", "Salt for water"],
      },
      {
        id: "2",
        instruction:
          "While pasta cooks, melt butter in a large skillet over medium heat. Add minced garlic and cook for 1 minute until fragrant.",
        duration: 2,
        ingredients: ["2 tbsp butter", "3 cloves garlic, minced"],
      },
      {
        id: "3",
        instruction:
          "Add heavy cream to the skillet and bring to a gentle simmer. Season with salt and pepper.",
        duration: 3,
        ingredients: ["1 cup heavy cream", "Salt and pepper to taste"],
      },
      {
        id: "4",
        instruction:
          "Drain pasta and add to the cream sauce. Toss with parmesan cheese until well coated. Serve immediately.",
        ingredients: ["1/2 cup grated parmesan cheese"],
      },
    ],
    ingredients: [
      "12 oz pasta",
      "2 tbsp butter",
      "3 cloves garlic, minced",
      "1 cup heavy cream",
      "1/2 cup grated parmesan cheese",
      "Salt and pepper to taste",
    ],
  },
  {
    id: "2",
    title: "Mediterranean Chicken Bowl",
    imageUrl:
      "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400",
    prepTime: 15,
    cookTime: 25,
    servings: 2,
    difficulty: "Medium" as const,
    tags: ["High Protein", "Mediterranean"],
    description:
      "Fresh and healthy Mediterranean-inspired chicken bowl with vegetables.",
    steps: [
      {
        id: "1",
        instruction:
          "Season chicken breasts with olive oil, lemon juice, oregano, salt and pepper. Let marinate for 15 minutes.",
        duration: 15,
      },
      {
        id: "2",
        instruction:
          "Heat a large skillet over medium-high heat. Cook chicken for 6-7 minutes per side until cooked through.",
        duration: 14,
      },
      {
        id: "3",
        instruction:
          "While chicken rests, prepare the vegetables and assemble bowls with rice, vegetables, and sliced chicken.",
        duration: 10,
      },
    ],
    ingredients: [
      "2 chicken breasts",
      "2 tbsp olive oil",
      "1 lemon, juiced",
      "1 tsp oregano",
      "1 cucumber, diced",
      "2 cups cherry tomatoes",
      "1/2 red onion, sliced",
      "1/4 cup kalamata olives",
      "2 cups cooked rice",
      "Salt and pepper",
    ],
  },
];

const sampleWeekData = [
  {
    date: "2024-01-15",
    dayName: "Monday",
    meals: {
      breakfast: { isEmpty: true },
      lunch: { recipe: sampleRecipes[1], isEmpty: false },
      dinner: { recipe: sampleRecipes[0], isEmpty: false },
    },
  },
  {
    date: "2024-01-16",
    dayName: "Tuesday",
    meals: {
      breakfast: { isEmpty: true },
      lunch: { isEmpty: true },
      dinner: { isEmpty: true },
    },
  },
  {
    date: "2024-01-17",
    dayName: "Wednesday",
    meals: {
      breakfast: { isEmpty: true },
      lunch: { recipe: sampleRecipes[0], isEmpty: false },
      dinner: { isEmpty: true },
    },
  },
];

export function RecipeExperienceDemo() {
  const [cookingRecipe, setCookingRecipe] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("browse");

  return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
          <div className="mb-8">
            <h1 className="mb-2 font-header text-3xl font-bold text-gray-900">
              Recipe Experience Demo
            </h1>
            <p className="text-gray-600">
              Phase 4 implementation: Recipe cards, cooking mode, meal planning,
              and shopping lists
            </p>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6"
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger
                value="browse"
                className="flex items-center space-x-2"
              >
                <ChefHat className="h-4 w-4" />
                <span>Browse Recipes</span>
              </TabsTrigger>
              <TabsTrigger
                value="cooking"
                className="flex items-center space-x-2"
              >
                <Play className="h-4 w-4" />
                <span>Cooking Mode</span>
              </TabsTrigger>
              <TabsTrigger
                value="planning"
                className="flex items-center space-x-2"
              >
                <Calendar className="h-4 w-4" />
                <span>Meal Planning</span>
              </TabsTrigger>
              <TabsTrigger
                value="shopping"
                className="flex items-center space-x-2"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>Shopping List</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="browse">
              <Card>
                <CardHeader>
                  <CardTitle>Recipe Cards</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {sampleRecipes.map((recipe) => (
                      <UnifiedRecipeCard
                        key={recipe.id}
                        recipe={{
                          id: recipe.id,
                          title: recipe.title,
                          imageUrl: recipe.imageUrl,
                          prepTime: recipe.prepTime,
                          cookTime: recipe.cookTime,
                          servings: recipe.servings,
                          difficulty: recipe.difficulty,
                          tags: recipe.tags,
                          description: recipe.description,
                          slug: recipe.id,
                          createdAt: new Date(),
                          updatedAt: new Date(),
                        }}
                        variant="detailed"
                        size="md"
                        onViewDetails={() =>
                          alert(`View details for: ${recipe.title}`)
                        }
                        onAddToPlan={(recipeId, dayIndex, mealType) =>
                          alert(`Added ${recipe.title} to meal plan`)
                        }
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cooking">
              <Card>
                <CardHeader>
                  <CardTitle>Cooking Mode Interface</CardTitle>
                </CardHeader>
                <CardContent>
                  {cookingRecipe ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">
                          Now Cooking: {cookingRecipe.title}
                        </h3>
                        <Button
                          onClick={() => setCookingRecipe(null)}
                          variant="outline"
                        >
                          Exit Cooking Mode
                        </Button>
                      </div>
                      <div className="overflow-hidden rounded-lg border">
                        <CookingInterface
                          recipe={cookingRecipe}
                          onClose={() => setCookingRecipe(null)}
                          onComplete={() => {
                            alert("Recipe completed! Great job!");
                            setCookingRecipe(null);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="py-12 text-center">
                      <ChefHat className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                      <h3 className="mb-2 text-lg font-semibold text-gray-900">
                        Start Cooking
                      </h3>
                      <p className="mb-4 text-gray-600">
                        Select a recipe from the Browse tab to begin cooking
                        mode.
                      </p>
                      <Button onClick={() => setActiveTab("browse")}>
                        Browse Recipes
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="planning">
              <MealPlanGrid
                weekData={sampleWeekData}
                onAddRecipe={(day, mealType) =>
                  alert(`Add recipe for ${mealType} on ${day}`)
                }
                onRecipeClick={(recipe) => alert(`Clicked on: ${recipe.title}`)}
                onRecipeRemove={(day, mealType) =>
                  alert(`Remove ${mealType} from ${day}`)
                }
              />
            </TabsContent>

            <TabsContent value="shopping">
              <ShoppingListGenerator
                recipes={sampleRecipes}
                onItemCheck={(itemId, checked) =>
                  console.log(`${itemId} ${checked ? "checked" : "unchecked"}`)
                }
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
  );
}
